import React from 'react';
import {
  Gesture,
  Today,
  Title,
  Person,
  Notes,
  CheckBox,
  FormatStrikethrough,
  LocationOn,
  LocationCity,
  LocalOffer,
  AccessTime,
  CropSquare,
} from '@material-ui/icons';
import type { ValueOf } from '@skyslope/utils';
import type { BlockType } from '../store/senderBuild/types';

export const SHELF_WIDTH = 360;
export const SKELETON_WIDTH = 8.5 * 72;
export const SKELETON_HEIGHT = 11 * 72;

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2.5;
export const ZOOM_INCREMENT = 0.25;

export const MIN_BLOCK_SIZE = 9;

export const ARROW_KEY_MOVEMENT_INCREMENT = 6;

export const priceComparisonLink = 'https://skyslope.com/digisign-price-comparison/';

export enum ResourceNames {
  ENVELOPE = 'envelope',
  TEMPLATE = 'template',
}

export type EnvelopeStatusType = 'All' | 'Completed' | 'Sent' | 'Draft' | 'InProgress' | 'Canceled' | 'Correcting';
export enum EnvelopeStatuses {
  ALL = 'All',
  COMPLETED = 'Completed',
  SENT = 'Sent',
  DRAFT = 'Draft',
  INPROGRESS = 'InProgress',
  CANCELED = 'Canceled',
  CORRECTING = 'Correcting',
}

export type SignerStatusType = 'Complete' | 'InProgress' | 'Declined';

export const BLOCK_TYPE_KEYS: Record<string, BlockType> = {
  SIGNATURE: 'Signature',
  INITIALS: 'Initials',
  DATE: 'DateSigned',
  FULL_NAME: 'FullName',
  TEXT: 'Text',
  CHECKBOX: 'Checkbox',
  MULTIPLE: 'Multiple',
  STRIKE: 'Strike',
  TIME: 'TimeSigned',
  RECTANGLE: 'Rectangle',
};

export const tagIcons = {
  locationOn: <LocationOn />,
  locationCity: <LocationCity />,
  person: <Person />,
  localOffer: <LocalOffer />,
};

export const readOnlyKey = 'readOnly' as const;
export const requiredKey = 'required' as const;

export type BlockProperty = {
  key: 'readOnly' | 'required' | 'value';
  label: string;
  flipValue?: boolean;
};

export const BLOCK_PROPERTIES: Record<BlockProperty['key'], BlockProperty> = {
  required: {
    key: 'required',
    label: 'Optional Field',
    flipValue: true,
  },
  readOnly: {
    key: 'readOnly',
    label: 'Read Only',
  },
  // @ts-expect-error -- I don't know if "value" or "checked" is correct
  checked: {
    key: 'value',
    label: 'Checked by Default',
  },
  value: {
    key: 'value',
    label: 'Checked by Default',
  },
};

export const MIN_CHECKBOX_SIZE = 2;
export const DEFAULT_CHECKBOX_SIZE = 12;
export const STRIKE_LINE_WIDTH = 2;

export const BLOCK_TYPES = [
  {
    key: BLOCK_TYPE_KEYS.MULTIPLE,
    name: 'Multiple',
  },
  {
    key: BLOCK_TYPE_KEYS.SIGNATURE,
    name: 'Signature',
    icon: <Gesture />,
    size: {
      default: {
        width: 116,
        height: 16,
      },
      min: {
        width: 44,
        height: 12,
      },
      max: {
        width: 336,
        height: 48,
      },
      fontSize: {
        default: 15,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
    },
    properties: [requiredKey],
    mobileOptions: ['Optional', 'Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.INITIALS,
    name: 'Initials',
    icon: <Title />,
    size: {
      default: {
        width: 20,
        height: 16,
      },
      min: {
        width: 16,
        height: 12,
      },
      max: {
        width: 96,
        height: 80,
      },
      fontSize: {
        default: 10,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
    },
    properties: [requiredKey],
    mobileOptions: ['Optional', 'Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.FULL_NAME,
    name: 'Full Name',
    icon: <Person />,
    size: {
      default: {
        width: 116,
        height: 16,
      },
      min: {
        width: 88,
        height: 12,
      },
      max: {
        width: 336,
        height: 48,
      },
      fontSize: {
        default: 11,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
    },
    properties: [],
    mobileOptions: ['Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.DATE,
    name: 'Date',
    icon: <Today />,
    size: {
      default: {
        width: 70,
        height: 16,
      },
      min: {
        width: 24,
        height: 12,
      },
      max: {
        width: 120,
        height: 24,
      },
      fontSize: {
        default: 10,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
    },
    properties: [],
    mobileOptions: ['Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.TIME,
    name: 'Time',
    icon: <AccessTime />,
    size: {
      default: {
        width: 116,
        height: 16,
      },
      min: {
        width: 24,
        height: 12,
      },
      max: {
        width: 120,
        height: 24,
      },
      fontSize: {
        default: 8,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
    },
    properties: [],
    mobileOptions: ['Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.CHECKBOX,
    name: 'Checkbox',
    icon: <CheckBox />,
    size: {
      default: {
        width: DEFAULT_CHECKBOX_SIZE,
        height: DEFAULT_CHECKBOX_SIZE,
      },
      min: {
        width: MIN_CHECKBOX_SIZE,
        height: MIN_CHECKBOX_SIZE,
      },
      max: {
        width: DEFAULT_CHECKBOX_SIZE * 5,
        height: DEFAULT_CHECKBOX_SIZE * 5,
      },
      fontSize: {
        default: 3,
      },
    },
    options: {
      assignRequired: true,
      hasText: false,
      checkboxGroupControls: true,
      readonly: false,
    },
    properties: [readOnlyKey, 'checked'],
    mobileOptions: ['Read Only', 'Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.TEXT,
    name: 'Text Field',
    icon: <Notes />,
    size: {
      default: {
        width: 116,
        height: 16,
      },
      min: {
        width: 24,
        height: 12,
      },
      max: {
        width: 10000,
        height: 10000,
      },
      fontSize: {
        default: 15,
      },
    },
    options: {
      assignRequired: true,
      hasText: true,
      optionalByDefault: true,
      readonly: true,
    },
    properties: [requiredKey, readOnlyKey],
    mobileOptions: ['Optional', 'Read Only', 'Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.STRIKE,
    name: 'Strike',
    icon: <FormatStrikethrough />,
    size: {
      default: {
        width: 20,
        height: STRIKE_LINE_WIDTH,
      },
      min: {
        width: 5,
        height: STRIKE_LINE_WIDTH,
      },
      max: {
        width: 10000,
        height: 10000,
      },
      fontSize: {
        default: null,
      },
    },
    options: {
      assignRequired: false,
      hasText: false,
    },
    properties: [],
    mobileOptions: ['Delete'],
  },
  {
    key: BLOCK_TYPE_KEYS.RECTANGLE,
    name: 'Rectangle',
    icon: <CropSquare />,
    size: {
      default: {
        width: 5,
        height: 5,
      },
      min: {
        width: 5,
        height: 5,
      },
      max: {
        width: 10000,
        height: 10000,
      },
      fontSize: {
        default: null,
      },
    },
    options: {
      assignRequired: false,
      hasText: false,
    },
    properties: [],
    mobileOptions: ['Delete'],
  },
];

export const SIGNER_COLORS = [
  '#F78812', // orange
  '#00ACC1', // teal
  '#845FDA', // purple
  '#079EFF', // blue
  '#FCCA4E', // yellow
  '#67D3BD', // green
  '#EF538B', // pink
];

export const EMAIL_BODY_LENGTH = 999;
export const EMAIL_SUBJECT_LENGTH = 99;

export const eventNames = {
  EnvelopeCreated: 'EnvelopeCreated',
  EnvelopeCanceled: 'EnvelopeCanceled',
  EnvelopeCompleted: 'EnvelopeCompleted',
  EnvelopeCorrecting: 'EnvelopeCorrecting',
  EnvelopeSent: 'EnvelopeSent',
  ESignDisclosureAccepted: 'ESignDisclosureAccepted',
  EnvelopeSigned: 'EnvelopeSigned',
  EnvelopeViewed: 'EnvelopeViewed',
  EnvelopeDownloaded: 'EnvelopeDownloaded',
  NotificationRequested: 'NotificationRequested',
  WaitingFor: 'WaitingFor',
  SignerUpdated: 'SignerUpdated',
  SignerAdded: 'SignerAdded',
  SignerDeleted: 'SignerDeleted',
  CarbonCopyAdded: 'CarbonCopyAdded',
  CarbonCopyDeleted: 'CarbonCopyDeleted',
  CarbonCopyUpdated: 'CarbonCopyUpdated',
  SignatureAccepted: 'SignatureAccepted',
  EnvelopeNameUpdated: 'EnvelopeNameUpdated',
};
export type EventName = ValueOf<typeof eventNames>;

export enum EmailTypes {
  ReviewAndSign = 'ReviewAndSign',
  Canceled = 'EnvelopeCanceled',
  Completed = 'EnvelopeCompleted',
}

export const signingGroupOrder = {
  0: 'First',
  1: 'Second',
  2: 'Third',
  3: 'Fourth',
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const chameleonElId = (num: number) => {
  return `ds-chameleon-${num}`;
};

export const statusOrder = {
  Active: 1,
  Incomplete: 2,
  Pending: 3,
  Expired: 4,
};

export const StrikeColors = {
  Black: '#000000',
  Red: '#cb0006',
  White: '#ffffff',
};
